import { useReducer, useState } from "react";
import { Link } from "react-router-dom";
import "./ultimateTexasHoldem.css";

const SUITS = [
  { id: "spades", symbol: "♠", color: "black" },
  { id: "hearts", symbol: "♥", color: "red" },
  { id: "diamonds", symbol: "♦", color: "red" },
  { id: "clubs", symbol: "♣", color: "black" },
];

const RANKS = [
  { rank: "2", value: 2 },
  { rank: "3", value: 3 },
  { rank: "4", value: 4 },
  { rank: "5", value: 5 },
  { rank: "6", value: 6 },
  { rank: "7", value: 7 },
  { rank: "8", value: 8 },
  { rank: "9", value: 9 },
  { rank: "10", value: 10 },
  { rank: "J", value: 11 },
  { rank: "Q", value: 12 },
  { rank: "K", value: 13 },
  { rank: "A", value: 14 },
];

const HAND_NAMES = [
  "High card",
  "One pair",
  "Two pair",
  "Three of a kind",
  "Straight",
  "Flush",
  "Full house",
  "Four of a kind",
  "Straight flush",
];

const SUIT_INDEXES = {
  spades: 0,
  hearts: 1,
  diamonds: 2,
  clubs: 3,
};
const SCORE_CATEGORY_SHIFT = 20;

const PHASES = {
  preflop: {
    label: "Pre-flop",
    prompt: "Raise four or see the flop?",
    actions: [
      { id: "raise", label: "Raise 4×", hint: "Max pressure" },
      { id: "check", label: "Check", hint: "See the flop" },
    ],
  },
  flop: {
    label: "The flop",
    prompt: "Commit two or check again?",
    actions: [
      { id: "raise", label: "Raise 2×", hint: "Make the Play bet" },
      { id: "check", label: "Check", hint: "See the river" },
    ],
  },
  river: {
    label: "The river",
    prompt: "Play the hand or let it go?",
    actions: [
      { id: "raise", label: "Raise 1×", hint: "Stay in" },
      { id: "fold", label: "Fold", hint: "Lose Ante + Blind" },
    ],
  },
};

const FOCUS_OPTIONS = [
  { id: "all", label: "Full hand" },
  { id: "preflop", label: "Pre-flop" },
  { id: "flop", label: "Flop" },
  { id: "river", label: "River" },
];

export function createDeck() {
  return SUITS.flatMap((suit, suitIndex) =>
    RANKS.map(({ rank, value }) => ({
      id: `${rank}-${suit.id}`,
      rank,
      value,
      suit: suit.id,
      suitIndex,
      symbol: suit.symbol,
      color: suit.color,
    }))
  );
}

function shuffle(cards) {
  const shuffled = [...cards];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
}

function chooseFive(cards) {
  const hands = [];

  function visit(start, chosen) {
    if (chosen.length === 5) {
      hands.push(chosen);
      return;
    }

    for (
      let index = start;
      index <= cards.length - (5 - chosen.length);
      index += 1
    ) {
      visit(index + 1, [...chosen, cards[index]]);
    }
  }

  visit(0, []);
  return hands;
}

function compareScores(first, second) {
  const length = Math.max(first.length, second.length);

  for (let index = 0; index < length; index += 1) {
    const difference = (first[index] || 0) - (second[index] || 0);

    if (difference !== 0) {
      return difference;
    }
  }

  return 0;
}

function scoreFive(cards) {
  const values = cards.map((card) => card.value).sort((a, b) => b - a);
  const uniqueValues = [...new Set(values)];
  const straightValues = uniqueValues.includes(14)
    ? [...uniqueValues, 1]
    : uniqueValues;
  let straightHigh = null;

  for (let index = 0; index <= straightValues.length - 5; index += 1) {
    const window = straightValues.slice(index, index + 5);

    if (window.every((value, offset) => value === window[0] - offset)) {
      straightHigh = window[0];
      break;
    }
  }

  const isFlush = cards.every((card) => card.suit === cards[0].suit);
  const groups = [...new Set(values)]
    .map((value) => ({
      value,
      count: values.filter((candidate) => candidate === value).length,
    }))
    .sort((a, b) => b.count - a.count || b.value - a.value);

  if (isFlush && straightHigh) {
    return [8, straightHigh];
  }

  if (groups[0].count === 4) {
    return [7, groups[0].value, groups[1].value];
  }

  if (groups[0].count === 3 && groups[1].count === 2) {
    return [6, groups[0].value, groups[1].value];
  }

  if (isFlush) {
    return [5, ...values];
  }

  if (straightHigh) {
    return [4, straightHigh];
  }

  if (groups[0].count === 3) {
    return [
      3,
      groups[0].value,
      ...groups.slice(1).map((group) => group.value),
    ];
  }

  if (groups[0].count === 2 && groups[1].count === 2) {
    return [
      2,
      Math.max(groups[0].value, groups[1].value),
      Math.min(groups[0].value, groups[1].value),
      groups[2].value,
    ];
  }

  if (groups[0].count === 2) {
    return [
      1,
      groups[0].value,
      ...groups.slice(1).map((group) => group.value),
    ];
  }

  return [0, ...values];
}

export function getBestHand(cards) {
  if (cards.length < 5) {
    return null;
  }

  const score = chooseFive(cards).reduce((best, hand) => {
    const candidate = scoreFive(hand);
    return !best || compareScores(candidate, best) > 0 ? candidate : best;
  }, null);

  return {
    category: score[0],
    name: HAND_NAMES[score[0]],
    score,
  };
}

function getStraightHigh(rankMask) {
  for (let high = 14; high >= 6; high -= 1) {
    const straightMask = 31 << (high - 6);
    if ((rankMask & straightMask) === straightMask) return high;
  }

  return (rankMask & 4111) === 4111 ? 5 : 0;
}

function countBits(value) {
  let count = 0;
  let remaining = value;

  while (remaining) {
    remaining &= remaining - 1;
    count += 1;
  }

  return count;
}

function addHighCards(score, rankMask, excludedMask, count, shift = 16) {
  let cardsAdded = 0;
  let result = score;

  for (let rank = 14; rank >= 2 && cardsAdded < count; rank -= 1) {
    const rankBit = 1 << (rank - 2);
    if ((rankMask & rankBit) && !(excludedMask & rankBit)) {
      result |= rank << (shift - cardsAdded * 4);
      cardsAdded += 1;
    }
  }

  return result;
}

function createBoardState(cards) {
  const counts = Array(15).fill(0);
  const suitMasks = [0, 0, 0, 0];
  let rankMask = 0;

  cards.forEach((card) => {
    const rankBit = 1 << (card.value - 2);
    const suitIndex = card.suitIndex ?? SUIT_INDEXES[card.suit];
    counts[card.value] += 1;
    rankMask |= rankBit;
    suitMasks[suitIndex] |= rankBit;
  });

  return { counts, rankMask, suitMasks };
}

function scoreBoardWithHole(boardState, firstCard, secondCard) {
  const firstRankBit = 1 << (firstCard.value - 2);
  const secondRankBit = 1 << (secondCard.value - 2);
  const firstSuit = firstCard.suitIndex ?? SUIT_INDEXES[firstCard.suit];
  const secondSuit = secondCard.suitIndex ?? SUIT_INDEXES[secondCard.suit];
  const rankMask = boardState.rankMask | firstRankBit | secondRankBit;
  let flushMask = 0;

  for (let suit = 0; suit < 4; suit += 1) {
    let suitMask = boardState.suitMasks[suit];
    if (firstSuit === suit) suitMask |= firstRankBit;
    if (secondSuit === suit) suitMask |= secondRankBit;

    if (countBits(suitMask) >= 5) {
      const straightFlushHigh = getStraightHigh(suitMask);
      if (straightFlushHigh) {
        return (8 << SCORE_CATEGORY_SHIFT) | (straightFlushHigh << 16);
      }
      flushMask = suitMask;
    }
  }

  let four = 0;
  let trips = 0;
  let highPair = 0;
  let secondPair = 0;

  for (let rank = 14; rank >= 2; rank -= 1) {
    const count =
      boardState.counts[rank] +
      (firstCard.value === rank ? 1 : 0) +
      (secondCard.value === rank ? 1 : 0);

    if (!four && count === 4) four = rank;
    if (!trips && count >= 3) trips = rank;
    if (count >= 2) {
      if (!highPair) highPair = rank;
      else if (!secondPair) secondPair = rank;
    }
  }

  if (four) {
    const fourBit = 1 << (four - 2);
    return addHighCards(
      (7 << SCORE_CATEGORY_SHIFT) | (four << 16),
      rankMask,
      fourBit,
      1,
      12
    );
  }

  if (trips) {
    let fullHousePair = 0;
    for (let rank = 14; rank >= 2; rank -= 1) {
      const count =
        boardState.counts[rank] +
        (firstCard.value === rank ? 1 : 0) +
        (secondCard.value === rank ? 1 : 0);
      if (rank !== trips && count >= 2) {
        fullHousePair = rank;
        break;
      }
    }
    if (fullHousePair) {
      return (
        (6 << SCORE_CATEGORY_SHIFT) |
        (trips << 16) |
        (fullHousePair << 12)
      );
    }
  }

  if (flushMask) {
    return addHighCards(5 << SCORE_CATEGORY_SHIFT, flushMask, 0, 5);
  }

  const straightHigh = getStraightHigh(rankMask);
  if (straightHigh) {
    return (4 << SCORE_CATEGORY_SHIFT) | (straightHigh << 16);
  }

  if (trips) {
    const tripsBit = 1 << (trips - 2);
    return addHighCards(
      (3 << SCORE_CATEGORY_SHIFT) | (trips << 16),
      rankMask,
      tripsBit,
      2,
      12
    );
  }

  if (secondPair) {
    const pairBits =
      (1 << (highPair - 2)) | (1 << (secondPair - 2));
    return addHighCards(
      (2 << SCORE_CATEGORY_SHIFT) |
        (highPair << 16) |
        (secondPair << 12),
      rankMask,
      pairBits,
      1,
      8
    );
  }

  if (highPair) {
    const pairBit = 1 << (highPair - 2);
    return addHighCards(
      (1 << SCORE_CATEGORY_SHIFT) | (highPair << 16),
      rankMask,
      pairBit,
      3,
      12
    );
  }

  return addHighCards(0, rankMask, 0, 5);
}

function formatHoleCards(holeCards) {
  return holeCards.map((card) => `${card.rank}${card.symbol}`).join(" ");
}

function getCardNumber(card) {
  return (card.value - 2) * 4 +
    (card.suitIndex ?? SUIT_INDEXES[card.suit]);
}

function createSeed(cards) {
  return cards
    .map(getCardNumber)
    .sort((first, second) => first - second)
    .reduce(
      (seed, cardNumber) =>
        Math.imul(seed ^ (cardNumber + 1), 16777619) >>> 0,
      2166136261
    );
}

function createRandom(seed) {
  let state = seed || 0x6d2b79f5;

  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value =
      (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function sampleCards(cards, count, random) {
  const sampled = [...cards];

  for (let index = 0; index < count; index += 1) {
    const swap =
      index + Math.floor(random() * (sampled.length - index));
    [sampled[index], sampled[swap]] = [sampled[swap], sampled[index]];
  }

  return sampled;
}

/**
 * A deterministic, nested Monte Carlo calculation for the pre-flop decision.
 * Each sampled flop makes one decision using only information available on
 * that flop; each sampled river likewise chooses between 1x and folding.
 * Keeping those decision points nested avoids the perfect-information bias of
 * choosing the best action after seeing the dealer's cards.
 */
export function calculatePreflopEquity(
  holeCards,
  deadCards = [],
  options = {}
) {
  const flopSamples = options.flopSamples || 640;
  const boardSamples = options.boardSamples || 24;
  const dealerSamples = options.dealerSamples || 32;
  const unseenCards = getUnseenCards(holeCards, [], deadCards);
  const random = createRandom(createSeed([...holeCards, ...deadCards]));
  const flopDeltas = [];
  let raiseReturn = 0;
  let checkReturn = 0;
  let total = 0;
  let flopsRaise2 = 0;
  let flopsCheck = 0;

  for (let flopIndex = 0; flopIndex < flopSamples; flopIndex += 1) {
    const flopDeck = sampleCards(unseenCards, 3, random);
    const flop = flopDeck.slice(0, 3);
    const afterFlop = flopDeck.slice(3);
    let flopRaise4Return = 0;
    let flopRaise2Return = 0;
    let flopCheckReturn = 0;
    let flopTotal = 0;

    for (let boardIndex = 0; boardIndex < boardSamples; boardIndex += 1) {
      const boardDeck = sampleCards(afterFlop, 2, random);
      const board = [...flop, boardDeck[0], boardDeck[1]];
      const afterBoard = boardDeck.slice(2);
      const boardState = createBoardState(board);
      const playerScore = scoreBoardWithHole(
        boardState,
        holeCards[0],
        holeCards[1]
      );
      const blindPayout = getBlindPayout(playerScore);
      let boardRaise1Return = 0;

      for (
        let dealerIndex = 0;
        dealerIndex < dealerSamples;
        dealerIndex += 1
      ) {
        const firstIndex = Math.floor(random() * afterBoard.length);
        let secondIndex = Math.floor(random() * (afterBoard.length - 1));
        if (secondIndex >= firstIndex) secondIndex += 1;
        const dealerScore = scoreBoardWithHole(
          boardState,
          afterBoard[firstIndex],
          afterBoard[secondIndex]
        );
        const comparison = playerScore - dealerScore;
        const dealerQualifies =
          dealerScore >>> SCORE_CATEGORY_SHIFT >= 1;

        flopRaise4Return += getShowdownReturn(
          comparison,
          dealerQualifies,
          blindPayout,
          4
        );
        flopRaise2Return += getShowdownReturn(
          comparison,
          dealerQualifies,
          blindPayout,
          2
        );
        boardRaise1Return += getShowdownReturn(
          comparison,
          dealerQualifies,
          blindPayout,
          1
        );
        flopTotal += 1;
      }

      flopCheckReturn += Math.max(
        boardRaise1Return,
        -2 * dealerSamples
      );
    }

    const bestFlopReturn = Math.max(
      flopRaise2Return,
      flopCheckReturn
    );
    if (flopRaise2Return >= flopCheckReturn) flopsRaise2 += 1;
    else flopsCheck += 1;

    raiseReturn += flopRaise4Return;
    checkReturn += bestFlopReturn;
    total += flopTotal;
    flopDeltas.push(
      (flopRaise4Return - bestFlopReturn) / flopTotal
    );
  }

  const raiseExpectedValue = raiseReturn / total;
  const checkExpectedValue = checkReturn / total;
  const raiseMinusCheck = raiseExpectedValue - checkExpectedValue;
  const sampleVariance =
    flopDeltas.reduce(
      (sum, value) => sum + (value - raiseMinusCheck) ** 2,
      0
    ) / Math.max(1, flopDeltas.length - 1);

  return {
    raiseExpectedValue,
    checkExpectedValue,
    raiseMinusCheck,
    standardError: Math.sqrt(sampleVariance / flopDeltas.length),
    total,
    flopSamples,
    boardSamples,
    dealerSamples,
    flopsRaise2,
    flopsCheck,
  };
}

const SOLVER_SUIT_INDEXES = {
  spades: 0,
  hearts: 1,
  clubs: 2,
  diamonds: 3,
};

function getSolverCardNumber(card) {
  return (
    (card.value - 2) * 4 + SOLVER_SUIT_INDEXES[card.suit]
  );
}

function solvePreflopRange(cards, workerIndex, workerCount) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(
      `${process.env.PUBLIC_URL}/uthSolverWorker.js`
    );

    worker.onmessage = ({ data }) => {
      worker.terminate();
      if (data.error) reject(new Error(data.error));
      else resolve(data);
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || "Exact pre-flop worker failed"));
    };
    worker.postMessage({ cards, workerIndex, workerCount });
  });
}

export async function calculateExactPreflopEquity(
  holeCards,
  deadCards,
  options = {}
) {
  if (deadCards.length !== 10) {
    throw new Error("Exact pre-flop calculation requires 10 dead cards");
  }
  if (typeof Worker === "undefined") {
    throw new Error("Web Workers are unavailable in this browser");
  }

  const availableWorkers =
    typeof navigator === "undefined"
      ? 4
      : navigator.hardwareConcurrency || 4;
  const workerCount = Math.min(
    10,
    Math.max(1, options.workerCount || availableWorkers)
  );
  const cards = [...holeCards, ...deadCards].map(getSolverCardNumber);
  const ranges = await Promise.all(
    Array.from({ length: workerCount }, (_, workerIndex) =>
      solvePreflopRange(cards, workerIndex, workerCount)
    )
  );
  const totals = ranges.reduce(
    (sum, result) => ({
      raise4Half: sum.raise4Half + result.raise4Half,
      checkHalf: sum.checkHalf + result.checkHalf,
      terminalDeals: sum.terminalDeals + result.terminalDeals,
      flopsRaise2: sum.flopsRaise2 + result.flopsRaise2,
      flopsCheck: sum.flopsCheck + result.flopsCheck,
    }),
    {
      raise4Half: 0,
      checkHalf: 0,
      terminalDeals: 0,
      flopsRaise2: 0,
      flopsCheck: 0,
    }
  );
  const denominator = 2 * totals.terminalDeals;
  const raiseExpectedValue = totals.raise4Half / denominator;
  const checkExpectedValue = totals.checkHalf / denominator;

  return {
    raiseExpectedValue,
    checkExpectedValue,
    raiseMinusCheck: raiseExpectedValue - checkExpectedValue,
    total: totals.terminalDeals,
    flopsRaise2: totals.flopsRaise2,
    flopsCheck: totals.flopsCheck,
    workerCount,
    exact: true,
  };
}

export function getExactPreflopDecision(equity) {
  const shouldRaise = equity.raiseMinusCheck >= 0;

  return {
    action: shouldRaise ? "raise" : "check",
    label: shouldRaise ? "Raise 4×" : "Check",
    reason: `All ten exact dead cards were included across ${equity.total.toLocaleString()} legal continuations. Raise 4× averages ${formatUnits(
      equity.raiseExpectedValue
    )} units versus ${formatUnits(
      equity.checkExpectedValue
    )} by checking, a ${formatPercent(
      equity.raiseMinusCheck
    )} difference. This exhaustive result includes suits and every blocked rank, optimizes 2× separately on all ${(
      equity.flopsRaise2 + equity.flopsCheck
    ).toLocaleString()} flops, and optimizes 1× versus folding on every river.`,
    equity,
  };
}

export function getPreflopDecision(holeCards, deadCards = []) {
  const [high, low] = [...holeCards].sort((a, b) => b.value - a.value);
  const isPair = high.value === low.value;
  const isSuited = high.suit === low.suit;
  let shouldRaise = false;
  let reason = "This hand sits outside the 4× opening range.";

  if (isPair) {
    shouldRaise = high.value >= 3;
    reason = shouldRaise
      ? `Pocket ${high.rank}s are in the 4× range (all pairs 3s or higher).`
      : "Pocket 2s are the only pair that checks before the flop.";
  } else if (isSuited) {
    shouldRaise =
      high.value === 14 ||
      high.value === 13 ||
      (high.value === 12 && low.value >= 6) ||
      (high.value === 11 && low.value >= 8);

    if (shouldRaise) {
      if (high.value === 14) reason = "Every suited ace belongs in the 4× range.";
      else if (high.value === 13) reason = "Every suited king belongs in the 4× range.";
      else if (high.value === 12) reason = "Suited Q–6 or better raises 4×.";
      else reason = "Suited J–8 or better raises 4×.";
    } else {
      reason = `${high.rank}${low.rank} suited falls below the suited 4× cutoff.`;
    }
  } else {
    shouldRaise =
      high.value === 14 ||
      (high.value === 13 && low.value >= 5) ||
      (high.value === 12 && low.value >= 8) ||
      (high.value === 11 && low.value === 10);

    if (shouldRaise) {
      if (high.value === 14) reason = "Every ace-high hand raises 4×.";
      else if (high.value === 13) reason = "Unsuited K–5 or better raises 4×.";
      else if (high.value === 12) reason = "Unsuited Q–8 or better raises 4×.";
      else reason = "J–10 is the offsuit jack-high hand in the 4× range.";
    }
  }

  if (deadCards.length === 10) {
    const equity = calculatePreflopEquity(holeCards, deadCards);
    const marginOfError = 1.96 * equity.standardError;
    const simulatedRaise = equity.raiseMinusCheck >= 0;

    return {
      action: simulatedRaise ? "raise" : "check",
      label: simulatedRaise ? "Raise 4×" : "Check",
      reason: `All ten exact dead cards are included in a deterministic nested simulation of ${equity.total.toLocaleString()} legal continuations. Raise 4× averages ${formatUnits(
        equity.raiseExpectedValue
      )} units versus ${formatUnits(
        equity.checkExpectedValue
      )} by checking, a ${formatPercent(
        equity.raiseMinusCheck
      )} difference (estimated 95% sampling error ±${formatPercent(
        marginOfError
      )}). This includes suits, multiple blocked pairing outs, and optimal sampled 2×/1× decisions.`,
      equity,
    };
  }

  if (deadCards.length) {
    reason += ` The standard range is used because the card-aware pre-flop calculation requires exactly 10 known dead cards; ${deadCards.length} were supplied.`;
  }

  return {
    action: shouldRaise ? "raise" : "check",
    label: shouldRaise ? "Raise 4×" : "Check",
    reason,
  };
}

function getUnseenCards(holeCards, board, deadCards = []) {
  const visibleIds = new Set(
    [...holeCards, ...board, ...deadCards].map((card) => card.id)
  );
  return createDeck().filter((card) => !visibleIds.has(card.id));
}

function getBlindPayout(score) {
  const category = score >>> SCORE_CATEGORY_SHIFT;
  if (category === 8 && ((score >>> 16) & 15) === 14) return 500;

  return {
    8: 50,
    7: 10,
    6: 3,
    5: 1.5,
    4: 1,
  }[category] || 0;
}

function getShowdownReturn(comparison, dealerQualifies, blindPayout, playBet) {
  if (comparison === 0) return 0;

  if (comparison > 0) {
    return playBet + (dealerQualifies ? 1 : 0) + blindPayout;
  }

  return -playBet - 1 - (dealerQualifies ? 1 : 0);
}

function addResult(results, comparison) {
  if (comparison > 0) results.wins += 1;
  else if (comparison < 0) results.losses += 1;
  else results.ties += 1;
  results.total += 1;
}

function withEquity(results) {
  return {
    ...results,
    equity: (results.wins + results.ties / 2) / results.total,
  };
}

export function calculateRiverEquity(holeCards, board, deadCards = []) {
  const unseenCards = getUnseenCards(holeCards, board, deadCards);
  const boardState = createBoardState(board);
  const playerScore = scoreBoardWithHole(
    boardState,
    holeCards[0],
    holeCards[1]
  );
  const blindPayout = getBlindPayout(playerScore);
  const results = { wins: 0, ties: 0, losses: 0, total: 0 };
  let raiseReturn = 0;

  for (let first = 0; first < unseenCards.length - 1; first += 1) {
    for (let second = first + 1; second < unseenCards.length; second += 1) {
      const dealerScore = scoreBoardWithHole(
        boardState,
        unseenCards[first],
        unseenCards[second]
      );
      const comparison = playerScore - dealerScore;

      addResult(results, comparison);
      raiseReturn += getShowdownReturn(
        comparison,
        dealerScore >>> SCORE_CATEGORY_SHIFT >= 1,
        blindPayout,
        1
      );
    }
  }

  return {
    ...withEquity(results),
    raiseExpectedValue: raiseReturn / results.total,
    foldExpectedValue: -2,
  };
}

export function calculateFlopEquity(holeCards, flop, deadCards = []) {
  const unseenCards = getUnseenCards(holeCards, flop, deadCards);
  const results = { wins: 0, ties: 0, losses: 0, total: 0 };
  let raiseReturn = 0;
  let checkReturn = 0;

  for (let turn = 0; turn < unseenCards.length - 1; turn += 1) {
    for (let river = turn + 1; river < unseenCards.length; river += 1) {
      const board = [...flop, unseenCards[turn], unseenCards[river]];
      const boardState = createBoardState(board);
      const remainingCards = unseenCards.filter(
        (_, index) => index !== turn && index !== river
      );
      const playerScore = scoreBoardWithHole(
        boardState,
        holeCards[0],
        holeCards[1]
      );
      const blindPayout = getBlindPayout(playerScore);
      let riverRaiseReturn = 0;
      let riverScenarios = 0;

      for (let first = 0; first < remainingCards.length - 1; first += 1) {
        for (
          let second = first + 1;
          second < remainingCards.length;
          second += 1
        ) {
          const dealerScore = scoreBoardWithHole(
            boardState,
            remainingCards[first],
            remainingCards[second]
          );
          const comparison = playerScore - dealerScore;
          const dealerQualifies =
            dealerScore >>> SCORE_CATEGORY_SHIFT >= 1;

          addResult(results, comparison);
          raiseReturn += getShowdownReturn(
            comparison,
            dealerQualifies,
            blindPayout,
            2
          );
          riverRaiseReturn += getShowdownReturn(
            comparison,
            dealerQualifies,
            blindPayout,
            1
          );
          riverScenarios += 1;
        }
      }

      checkReturn += Math.max(riverRaiseReturn, -2 * riverScenarios);
    }
  }

  return {
    ...withEquity(results),
    raiseExpectedValue: raiseReturn / results.total,
    checkExpectedValue: checkReturn / results.total,
  };
}

function formatPercent(value) {
  return `${(value * 100).toFixed(2)}%`;
}

function formatUnits(value) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(3)}`;
}

export function getFlopDecision(holeCards, flop, deadCards = []) {
  const hand = getBestHand([...holeCards, ...flop]);
  const equity = calculateFlopEquity(holeCards, flop, deadCards);
  const shouldRaise = equity.raiseExpectedValue >= equity.checkExpectedValue;
  const deadCardNote = deadCards.length
    ? ` after excluding ${deadCards.length} known dead cards`
    : "";

  return {
    action: shouldRaise ? "raise" : "check",
    label: shouldRaise ? "Raise 2×" : "Check",
    reason: `${formatPercent(
      equity.equity
    )} exact showdown equity across ${equity.total.toLocaleString()} dealer/runout combinations${deadCardNote}. Raising averages ${formatUnits(
      equity.raiseExpectedValue
    )} units versus ${formatUnits(equity.checkExpectedValue)} by checking.`,
    hand,
    equity,
  };
}

export function getRiverDecision(holeCards, board, deadCards = []) {
  const hand = getBestHand([...holeCards, ...board]);
  const equity = calculateRiverEquity(holeCards, board, deadCards);
  const shouldRaise = equity.raiseExpectedValue >= equity.foldExpectedValue;
  const deadCardNote = deadCards.length
    ? ` after excluding ${deadCards.length} known dead cards`
    : "";

  return {
    action: shouldRaise ? "raise" : "fold",
    label: shouldRaise ? "Raise 1×" : "Fold",
    reason: `${formatPercent(
      equity.equity
    )} exact showdown equity across ${equity.total.toLocaleString()} dealer hands${deadCardNote}. Raising averages ${formatUnits(
      equity.raiseExpectedValue
    )} units versus ${formatUnits(equity.foldExpectedValue)} by folding.`,
    hand,
    equity,
  };
}

export function getDecision(phase, holeCards, board, deadCards = []) {
  if (phase === "preflop") {
    return getPreflopDecision(holeCards, deadCards);
  }
  if (phase === "flop") {
    return getFlopDecision(holeCards, board.slice(0, 3), deadCards);
  }
  return getRiverDecision(holeCards, board, deadCards);
}

function createRound(focus = "all", deal = 1, sharedCards = false) {
  const deck = shuffle(createDeck());

  return {
    deal,
    phase: focus === "all" ? "preflop" : focus,
    holeCards: deck.slice(0, 2),
    board: deck.slice(2, 7),
    deadCards: sharedCards ? deck.slice(7, 17) : [],
    feedback: null,
  };
}

function createInitialState() {
  return {
    focus: "all",
    sharedCards: false,
    round: createRound(),
    stats: {
      decisions: 0,
      correct: 0,
      streak: 0,
      bestStreak: 0,
    },
  };
}

function nextPhase(phase) {
  return phase === "preflop" ? "flop" : "river";
}

function trainerReducer(state, action) {
  if (action.type === "answer" && !state.round.feedback) {
    const decision =
      action.decision ||
      getDecision(
        state.round.phase,
        state.round.holeCards,
        state.round.board,
        state.round.deadCards
      );
    const isCorrect = action.choice === decision.action;
    const streak = isCorrect ? state.stats.streak + 1 : 0;

    return {
      ...state,
      round: {
        ...state.round,
        feedback: {
          ...decision,
          isCorrect,
          choice: action.choice,
        },
      },
      stats: {
        decisions: state.stats.decisions + 1,
        correct: state.stats.correct + (isCorrect ? 1 : 0),
        streak,
        bestStreak: Math.max(state.stats.bestStreak, streak),
      },
    };
  }

  if (action.type === "continue") {
    const canAdvance =
      state.focus === "all" &&
      state.round.feedback?.isCorrect &&
      state.round.feedback.action === "check" &&
      state.round.phase !== "river";

    if (canAdvance) {
      return {
        ...state,
        round: {
          ...state.round,
          phase: nextPhase(state.round.phase),
          feedback: null,
        },
      };
    }

    return {
      ...state,
      round: createRound(
        state.focus,
        state.round.deal + 1,
        state.sharedCards
      ),
    };
  }

  if (action.type === "set-focus") {
    return {
      ...state,
      focus: action.focus,
      round: createRound(
        action.focus,
        state.round.deal + 1,
        state.sharedCards
      ),
    };
  }

  if (action.type === "set-shared-cards") {
    return {
      ...state,
      sharedCards: action.enabled,
      round: createRound(
        state.focus,
        state.round.deal + 1,
        action.enabled
      ),
    };
  }

  if (action.type === "reset") {
    return {
      ...createInitialState(),
      focus: state.focus,
      sharedCards: state.sharedCards,
      round: createRound(
        state.focus,
        state.round.deal + 1,
        state.sharedCards
      ),
    };
  }

  if (action.type === "new-deal") {
    return {
      ...state,
      round: createRound(
        state.focus,
        state.round.deal + 1,
        state.sharedCards
      ),
    };
  }

  return state;
}

function PlayingCard({ card, hidden = false, empty = false, delay = 0 }) {
  if (empty) {
    return <span className="uthCard uthCardEmpty" aria-hidden="true" />;
  }

  if (hidden) {
    return (
      <span className="uthCard uthCardBack" aria-label="Face-down dealer card">
        <span aria-hidden="true">UTH</span>
      </span>
    );
  }

  return (
    <span
      className={`uthCard uthCardFront uthCard${
        card.color === "red" ? "Red" : "Black"
      }`}
      style={{ "--card-delay": `${delay}ms` }}
      aria-label={`${card.rank} of ${card.suit}`}
    >
      <span className="uthCardCorner">
        <strong>{card.rank}</strong>
        <span>{card.symbol}</span>
      </span>
      <span className="uthCardSuit" aria-hidden="true">
        {card.symbol}
      </span>
      <span className="uthCardCorner uthCardCornerBottom" aria-hidden="true">
        <strong>{card.rank}</strong>
        <span>{card.symbol}</span>
      </span>
    </span>
  );
}

function BetSpot({ label, value, active }) {
  return (
    <span className={`uthBetSpot ${active ? "uthBetSpotActive" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </span>
  );
}

function Feedback({ feedback, onContinue, canAdvance }) {
  return (
    <div
      className={`uthFeedback uthFeedback${
        feedback.isCorrect ? "Correct" : "Incorrect"
      }`}
      role="status"
    >
      <span className="uthFeedbackMark" aria-hidden="true">
        {feedback.isCorrect ? "✓" : "×"}
      </span>
      <div>
        <span className="uthFeedbackEyebrow">
          {feedback.isCorrect ? "Correct decision" : "Better play"}
        </span>
        <strong>{feedback.label}</strong>
        <p>{feedback.reason}</p>
      </div>
      <button type="button" onClick={onContinue}>
        {canAdvance ? "Reveal next street" : "Next deal"}
        <span aria-hidden="true">→</span>
      </button>
    </div>
  );
}

function UltimateTexasHoldemPage() {
  const [state, dispatch] = useReducer(
    trainerReducer,
    undefined,
    createInitialState
  );
  const [isSolving, setIsSolving] = useState(false);
  const phase = PHASES[state.round.phase];
  const visibleBoardCount =
    state.round.phase === "preflop" ? 0 : state.round.phase === "flop" ? 3 : 5;
  const currentHand =
    visibleBoardCount >= 3
      ? getBestHand([
          ...state.round.holeCards,
          ...state.round.board.slice(0, visibleBoardCount),
        ])
      : null;
  const canAdvance =
    state.focus === "all" &&
    state.round.feedback?.isCorrect &&
    state.round.feedback.action === "check" &&
    state.round.phase !== "river";

  async function answer(choice) {
    if (isSolving || state.round.feedback) return;

    if (
      state.round.phase === "preflop" &&
      state.round.deadCards.length === 10
    ) {
      setIsSolving(true);
      let decision;

      try {
        const equity = await calculateExactPreflopEquity(
          state.round.holeCards,
          state.round.deadCards
        );
        decision = getExactPreflopDecision(equity);
      } catch (error) {
        decision = getPreflopDecision(
          state.round.holeCards,
          state.round.deadCards
        );
        decision.reason = `The exhaustive browser solver was unavailable (${error.message}); this result uses the deterministic nested fallback. ${decision.reason}`;
      }

      dispatch({ type: "answer", choice, decision });
      setIsSolving(false);
      return;
    }

    dispatch({ type: "answer", choice });
  }

  return (
    <main className="uthPage">
      <div className="uthShell">
        <header className="uthTopbar">
          <div className="uthBrand">
            <Link to="/" aria-label="Back to home">
              H
            </Link>
            <div>
              <span>Decision trainer</span>
              <strong>Ultimate Texas Hold’em</strong>
            </div>
          </div>

          <div className="uthHeaderActions">
            <Link className="uthHeaderLink" to="/ultimate-texas-holdem/chart">
              Blocker chart
            </Link>
            <div className="uthTopStats" aria-label="Session results">
            <span>
              <strong>{state.stats.correct}</strong>
              correct
            </span>
            <span>
              <strong>{state.stats.streak}</strong>
              streak
            </span>
            <span>
              <strong>{state.stats.bestStreak}</strong>
              best
            </span>
            </div>
          </div>
        </header>

        <div className="uthLayout">
          <section className="uthTablePanel" aria-label="Training table">
            <div className="uthTableToolbar">
              <div className="uthStreetTrack" aria-label="Decision street">
                {Object.entries(PHASES).map(([id, item], index) => (
                  <span
                    key={id}
                    className={
                      id === state.round.phase
                        ? "uthStreetActive"
                        : Object.keys(PHASES).indexOf(state.round.phase) > index
                        ? "uthStreetComplete"
                        : ""
                    }
                  >
                    <i>{index + 1}</i>
                    {item.label}
                  </span>
                ))}
              </div>
              <div className="uthToolbarActions">
                <button
                  className={`uthSharedToggle ${
                    state.sharedCards ? "uthSharedToggleActive" : ""
                  }`}
                  type="button"
                  disabled={isSolving}
                  aria-pressed={state.sharedCards}
                  onClick={() =>
                    dispatch({
                      type: "set-shared-cards",
                      enabled: !state.sharedCards,
                    })
                  }
                >
                  <span className="uthSharedIcon" aria-hidden="true">
                    ♣
                  </span>
                  <span>
                    Known cards
                    <small>
                      {state.sharedCards ? "5 hands · 10 dead" : "Off"}
                    </small>
                  </span>
                </button>
                <div className="uthCompactFocus" aria-label="Training mode">
                  {FOCUS_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      className={
                        state.focus === option.id ? "uthFocusActive" : ""
                      }
                      type="button"
                      disabled={isSolving}
                      aria-pressed={state.focus === option.id}
                      onClick={() =>
                        dispatch({ type: "set-focus", focus: option.id })
                      }
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <button
                  className="uthNewDeal"
                  type="button"
                  disabled={isSolving}
                  onClick={() => dispatch({ type: "new-deal" })}
                >
                  New deal
                  <span aria-hidden="true">↻</span>
                </button>
              </div>
            </div>

            {state.sharedCards && (
              <div
                className="uthDeadCardsPanel"
                aria-label="Known dead hole cards"
              >
                <div className="uthDeadCardsCopy">
                  <span>Known dead cards</span>
                  <strong>Five exposed hands</strong>
                  <small>Full-card math: ranks, suits, and all blockers</small>
                </div>
                <div className="uthDeadHands">
                  {Array.from({ length: 5 }, (_, handIndex) => {
                    const hand = state.round.deadCards.slice(
                      handIndex * 2,
                      handIndex * 2 + 2
                    );

                    return (
                      <div className="uthDeadHand" key={handIndex}>
                        <span>Player {handIndex + 2}</span>
                        <div className="uthDeadHoleCards">
                          {hand.map((card, cardIndex) => (
                            <PlayingCard
                              key={`${state.round.deal}-${card.id}`}
                              card={card}
                              delay={(handIndex * 2 + cardIndex) * 35}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="uthFelt">
              <div className="uthDealer">
                <span className="uthZoneLabel">Dealer</span>
                <div className="uthDealerCards">
                  <PlayingCard hidden />
                  <PlayingCard hidden />
                </div>
              </div>

              <div className="uthCommunity">
                <span className="uthZoneLabel">Community</span>
                <div className="uthBoardCards">
                  {state.round.board.map((card, index) => (
                    <PlayingCard
                      key={`${state.round.deal}-${card.id}`}
                      card={card}
                      empty={index >= visibleBoardCount}
                      delay={index * 70}
                    />
                  ))}
                </div>
              </div>

              <div className="uthPlayerArea">
                <div className="uthBetRail" aria-label="Table bets">
                  <BetSpot label="Ante" value="1" active />
                  <BetSpot label="Blind" value="1" active />
                  <BetSpot
                    label="Play"
                    value={
                      state.round.feedback?.action === "raise"
                        ? state.round.phase === "preflop"
                          ? "4"
                          : state.round.phase === "flop"
                          ? "2"
                          : "1"
                        : "—"
                    }
                    active={state.round.feedback?.action === "raise"}
                  />
                </div>

                <div className="uthPlayerHand">
                  <span className="uthZoneLabel">Your hand</span>
                  <div className="uthHoleCards">
                    {state.round.holeCards.map((card, index) => (
                      <PlayingCard
                        key={`${state.round.deal}-${card.id}`}
                        card={card}
                        delay={index * 80}
                      />
                    ))}
                  </div>
                  <strong className="uthHandName">
                    {currentHand?.name || formatHoleCards(state.round.holeCards)}
                  </strong>
                </div>

                <span className="uthDealNumber">
                  Deal {String(state.round.deal).padStart(2, "0")}
                </span>
              </div>
            </div>

            <div className="uthDecisionPanel" aria-busy={isSolving}>
              {state.round.feedback ? (
                <Feedback
                  feedback={state.round.feedback}
                  canAdvance={canAdvance}
                  onContinue={() => dispatch({ type: "continue" })}
                />
              ) : (
                <>
                  <div className="uthPrompt">
                    <span>{phase.label} decision</span>
                    <h2>
                      {isSolving
                        ? "Solving all 3.9 billion continuations…"
                        : phase.prompt}
                    </h2>
                  </div>
                  <div className="uthActions">
                    {phase.actions.map((action, index) => (
                      <button
                        key={action.id}
                        className={index === 0 ? "uthActionPrimary" : ""}
                        type="button"
                        disabled={isSolving}
                        onClick={() => answer(action.id)}
                      >
                        <span>{action.label}</span>
                        <small>{action.hint}</small>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}

export default UltimateTexasHoldemPage;
